import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Textarea } from '../../ui/textarea';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { Loader2, Users, Home, Plane } from 'lucide-react';

import { LocationComboBox } from '../LocationComboBox';
import { GAME_CATEGORIES } from './constants';

/**
 * Create Game dialog (iter279 extraction from TournamentGames.js).
 *
 * Handles two modes — external matchup (single age category, opponent
 * required) and internal practice (multi-age, no opponent). Mode is
 * driven entirely by `formData.is_internal`.
 */
const CreateGameDialog = ({
  open,
  onOpenChange,
  tournament,
  formData,
  setFormData,
  availableTeams,
  onCreate,
  submitting,
}) => {
  const ageCats = tournament?.age_categories || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add Game</DialogTitle>
          <DialogDescription>
            Schedule a new game for {tournament?.name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Internal Game Checkbox */}
          <div className="flex items-center space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Checkbox
              id="is_internal"
              checked={formData.is_internal}
              onCheckedChange={(checked) => setFormData({
                ...formData,
                is_internal: checked,
                age_category: '',
                age_categories: [],
                team_id: '',
                opponent_team: checked ? '' : formData.opponent_team,
              })}
            />
            <div>
              <Label htmlFor="is_internal" className="font-medium cursor-pointer">
                Internal Practice Game
              </Label>
              <p className="text-xs text-slate-500">
                Select multiple age categories for internal team practice (no opponent)
              </p>
            </div>
          </div>

          {/* Age Category Selection — different for internal vs external */}
          {formData.is_internal ? (
            <div className="bg-slate-50 rounded-lg p-3 border">
              <Label className="text-sm font-medium text-slate-600 mb-2 block">
                Select Age Categories *
              </Label>
              <p className="text-xs text-slate-500 mb-3">
                All players registered in selected categories can RSVP
              </p>
              <div className="space-y-2">
                {ageCats.map(cat => (
                  <div
                    key={cat}
                    className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                      formData.age_categories.includes(cat)
                        ? 'bg-blue-100 border border-blue-300'
                        : 'bg-white border hover:bg-slate-100'
                    }`}
                    onClick={() => {
                      const newCategories = formData.age_categories.includes(cat)
                        ? formData.age_categories.filter(c => c !== cat)
                        : [...formData.age_categories, cat];
                      setFormData({ ...formData, age_categories: newCategories });
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.age_categories.includes(cat)}
                        onCheckedChange={(checked) => {
                          const newCategories = checked
                            ? [...formData.age_categories, cat]
                            : formData.age_categories.filter(c => c !== cat);
                          setFormData({ ...formData, age_categories: newCategories });
                        }}
                      />
                      <span className="font-medium">{cat.toUpperCase()}</span>
                    </div>
                  </div>
                ))}
              </div>
              {formData.age_categories.length > 0 && (
                <div className="mt-3 text-sm text-blue-600">
                  Selected: {formData.age_categories.map(c => c.toUpperCase()).join(', ')}
                </div>
              )}
            </div>
          ) : (
            <>
              <div>
                <Label>Age Category *</Label>
                <Select
                  value={formData.age_category}
                  onValueChange={(value) => setFormData({ ...formData, age_category: value, team_id: '' })}
                >
                  <SelectTrigger data-testid="game-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {ageCats.map(cat => (
                      <SelectItem key={cat} value={cat}>
                        {cat.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Available Teams for Selected Category — only for external games */}
              {formData.age_category && (
                <div className="bg-slate-50 rounded-lg p-3 border">
                  <Label className="text-sm font-medium text-slate-600 mb-2 block">
                    Available Teams for {formData.age_category.toUpperCase()}
                  </Label>
                  {availableTeams.length === 0 ? (
                    <p className="text-sm text-amber-600 flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      No teams created for this category yet
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {availableTeams.map(team => (
                        <div
                          key={team.id}
                          className={`flex items-center justify-between p-2 rounded cursor-pointer transition-colors ${
                            formData.team_id === team.id
                              ? 'bg-blue-100 border border-blue-300'
                              : 'bg-white border hover:bg-slate-100'
                          }`}
                          onClick={() => setFormData({ ...formData, team_id: team.id })}
                        >
                          <div className="flex items-center gap-2">
                            <input
                              type="radio"
                              checked={formData.team_id === team.id}
                              onChange={() => setFormData({ ...formData, team_id: team.id })}
                              className="h-4 w-4 text-blue-600"
                            />
                            <div>
                              <span className="font-medium">{team.name}</span>
                              {team.division && (
                                <Badge variant="outline" className="ml-2 text-xs">{team.division}</Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-sm text-slate-500">
                            {team.player_count} player{team.player_count !== 1 ? 's' : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Game Date *</Label>
              <Input
                type="date"
                value={formData.game_date}
                onChange={(e) => setFormData({ ...formData, game_date: e.target.value })}
                data-testid="game-date-input"
              />
            </div>
            <div>
              <Label>Start Time *</Label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                data-testid="game-time-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Reporting Time</Label>
              <Input
                type="time"
                value={formData.reporting_time}
                onChange={(e) => setFormData({ ...formData, reporting_time: e.target.value })}
                placeholder="When players should arrive"
                data-testid="game-reporting-time-input"
              />
              <p className="text-xs text-slate-500 mt-1">When players should arrive</p>
            </div>
            <div>
              <Label>RSVP Deadline</Label>
              <Input
                type="datetime-local"
                value={formData.rsvp_deadline}
                onChange={(e) => setFormData({ ...formData, rsvp_deadline: e.target.value })}
                data-testid="game-rsvp-deadline-input"
              />
              <p className="text-xs text-slate-500 mt-1">RSVPs close after this time</p>
            </div>
          </div>

          <div>
            <Label>Game Category</Label>
            <Select
              value={formData.game_category}
              onValueChange={(value) => setFormData({ ...formData, game_category: value })}
            >
              <SelectTrigger data-testid="game-category-select">
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent>
                {GAME_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Opponent Team — only for external games */}
          {!formData.is_internal && (
            <div>
              <Label>Opponent Team *</Label>
              <Input
                value={formData.opponent_team}
                onChange={(e) => setFormData({ ...formData, opponent_team: e.target.value })}
                placeholder="e.g., Springfield Lions"
                data-testid="game-opponent-input"
              />
            </div>
          )}

          <div>
            <Label>Location *</Label>
            <LocationComboBox
              value={formData.location}
              locationUrl={formData.location_url}
              onChange={(val) => setFormData(prev => ({ ...prev, location: val }))}
              onLocationUrlChange={(val) => setFormData(prev => ({ ...prev, location_url: val }))}
            />
          </div>

          <div>
            <Label>Venue Type *</Label>
            <Select
              value={formData.game_type}
              onValueChange={(value) => setFormData({ ...formData, game_type: value })}
            >
              <SelectTrigger data-testid="game-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="home">
                  <div className="flex items-center gap-2">
                    <Home className="h-4 w-4" />
                    Home
                  </div>
                </SelectItem>
                <SelectItem value="away">
                  <div className="flex items-center gap-2">
                    <Plane className="h-4 w-4" />
                    Away
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Notes (Optional)</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional details about the game..."
              data-testid="game-notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onCreate} disabled={submitting} data-testid="save-game-btn">
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Game
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CreateGameDialog;
